import { useEffect, useState } from 'react';
import type { Editor } from '@tiptap/react';
import type { Doc as YDoc } from 'yjs';

import { observeDocumentSettings } from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';
import { fitZoomPercent } from 'containers/Tiptap/components/tiptap-ui/zoom-controls/zoom-fit';

const FULL_ZOOM_PERCENT = 100;

export function useFitPagesToWidth(editor: Editor | null, enabled: boolean, ydoc?: YDoc | null): void {
  const [isMounted, setIsMounted] = useState(Boolean(editor?.isInitialized && !editor.isDestroyed));

  useEffect(() => {
    if (!editor) {
      setIsMounted(false);
      return undefined;
    }
    setIsMounted(editor.isInitialized && !editor.isDestroyed);
    const onCreate = (): void => setIsMounted(true);
    const onDestroy = (): void => setIsMounted(false);
    editor.on('create', onCreate);
    editor.on('destroy', onDestroy);
    return () => {
      editor.off('create', onCreate);
      editor.off('destroy', onDestroy);
    };
  }, [editor]);

  useEffect(() => {
    if (!editor || !isMounted || !enabled || typeof ResizeObserver === 'undefined') return undefined;
    const container = editor.view.dom.parentElement;
    if (!container) return undefined;

    const fit = (): void => {
      const pageWidthPx = (editor.storage?.pages?.pageFormat as { width?: number } | undefined)?.width;
      if (typeof pageWidthPx !== 'number' || editor.isDestroyed) return;
      const percent = Math.min(
        FULL_ZOOM_PERCENT,
        fitZoomPercent({ availableWidthPx: container.clientWidth, pageWidthPx })
      );
      editor.commands.setZoom?.(percent / 100);
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    const unobserveSettings = ydoc ? observeDocumentSettings(ydoc, fit) : undefined;
    return () => {
      observer.disconnect();
      unobserveSettings?.();
    };
  }, [editor, isMounted, enabled, ydoc]);
}
